import * as XLSX from "xlsx";

// Same shape the "View answers" modal reads, so the sheet matches what the admin sees on screen.
const questionTextOf = (item, idx) =>
  item?.question?.text ||
  item?.question?.questionText ||
  item?.questionText ||
  (typeof item?.question === "string" ? item.question : null) ||
  `Question ${idx + 1}`;

const answerTextOf = (item) => {
  const raw = item?.selectedOptions || item?.options || item?.answers || item?.selectedOptionIds || [];
  const labels = (Array.isArray(raw) ? raw : [raw])
    .filter((o) => o !== undefined && o !== null && o !== "")
    .map((o) => o?.text || o?.optionText || o?.label || o?.value || String(o));
  const typed = typeof item?.text === "string" ? item.text.trim() : "";
  if (typed) labels.push(typed);
  return labels.join(", ");
};

const STATUS_LABEL = { completed: "Completed", in_progress: "In Progress", details_only: "Details Only" };

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const submissionRoute = (s) => s.questionnaire?.pageRoute || s.pageRoute || "";

// Search used by the table; the export applies the same rule so "what you filtered is what you get".
export const matchesSearch = (s, term) => {
  const q = (term || "").trim().toLowerCase();
  if (!q) return true;
  return (
    (s.questionnaire?.name || s.questionnaireName || "").toLowerCase().includes(q) ||
    (s.userEmail || s.email || "").toLowerCase().includes(q) ||
    (s.userName || "").toLowerCase().includes(q) ||
    (s.userPhone || "").toLowerCase().includes(q) ||
    (s.userId || "").toLowerCase().includes(q) ||
    submissionRoute(s).toLowerCase().includes(q)
  );
};

const fitColumns = (rows, headers) =>
  headers.map((h) => ({
    wch: Math.min(
      60,
      Math.max(h.length, ...rows.map((r) => String(r[h] ?? "").length)) + 2
    ),
  }));

/**
 * Builds the workbook:
 *  - "Submissions": one row per submission (who, which questionnaire, status, when)
 *  - "Answers": one row per answered question, linked back by the "#" column
 *  - one sheet per questionnaire: a row per submission and a column per question with the
 *    option(s) chosen, so the sales team can read a whole enquiry on one line
 */
export const buildSubmissionsWorkbook = (submissions) => {
  const submissionRows = [];
  const answerRows = [];

  submissions.forEach((s, i) => {
    const n = i + 1;
    const answers = Array.isArray(s.answers) ? s.answers : [];
    submissionRows.push({
      "#": n,
      Questionnaire: s.questionnaire?.name || s.questionnaireName || "",
      Name: s.userName || "",
      Email: s.userEmail || s.email || "",
      Phone: s.userPhone || "",
      "User Type": s.userId ? "Customer" : "Guest",
      "Page Route": submissionRoute(s),
      Status: STATUS_LABEL[s.status] || s.status || "Completed",
      "No. of Answers": s.answers ? answers.length : s.answersCount ?? "",
      Country: s.countryCode || "",
      "Submitted At": formatDate(s.submittedAt || s.createdAt),
      "Submission ID": s.id || "",
    });

    answers.forEach((item, idx) => {
      answerRows.push({
        "#": n,
        Questionnaire: s.questionnaire?.name || s.questionnaireName || "",
        Name: s.userName || "",
        Email: s.userEmail || s.email || "",
        "Q No.": idx + 1,
        Question: questionTextOf(item, idx),
        Answer: answerTextOf(item),
      });
    });
  });

  const subHeaders = Object.keys(
    submissionRows[0] || {
      "#": 0, Questionnaire: 0, Name: 0, Email: 0, Phone: 0, "User Type": 0, "Page Route": 0,
      Status: 0, "No. of Answers": 0, Country: 0, "Submitted At": 0, "Submission ID": 0,
    }
  );
  const ansHeaders = ["#", "Questionnaire", "Name", "Email", "Q No.", "Question", "Answer"];

  const wsSubmissions = XLSX.utils.json_to_sheet(submissionRows, { header: subHeaders });
  wsSubmissions["!cols"] = fitColumns(submissionRows, subHeaders);
  wsSubmissions["!freeze"] = { xSplit: 0, ySplit: 1 };

  const wsAnswers = XLSX.utils.json_to_sheet(answerRows, { header: ansHeaders });
  wsAnswers["!cols"] = fitColumns(answerRows, ansHeaders);
  wsAnswers["!freeze"] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsSubmissions, "Submissions");
  XLSX.utils.book_append_sheet(wb, wsAnswers, "Answers");
  addQuestionnaireSheets(wb, submissions, new Set(["submissions", "answers"]));
  return { wb, submissionCount: submissionRows.length, answerCount: answerRows.length };
};

const safeSheetName = (name, used) => {
  const base = (String(name || "Questionnaire").replace(/[[\]:*?/\\]/g, " ").trim() || "Questionnaire").slice(0, 28);
  let candidate = base;
  let n = 2;
  while (used.has(candidate.toLowerCase())) {
    candidate = `${base.slice(0, 26)} ${n}`;
    n += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
};

// One sheet per questionnaire: a row per submission, a column per question, the chosen answer in the cell.
// Questions that only appear on some paths (follow-ups) are blank where they were not asked.
const addQuestionnaireSheets = (wb, submissions, usedNames) => {
  const groups = new Map();
  submissions.forEach((s) => {
    const key = s.questionnaire?.name || s.questionnaireName || "Questionnaire";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });

  groups.forEach((list, name) => {
    const questions = [];
    const seen = new Set();
    const rows = list.map((s, i) => {
      const row = {
        "#": i + 1,
        Name: s.userName || "",
        Email: s.userEmail || s.email || "",
        Phone: s.userPhone || "",
        "User Type": s.userId ? "Customer" : "Guest",
        Status: STATUS_LABEL[s.status] || s.status || "Completed",
        "Submitted At": formatDate(s.submittedAt || s.createdAt),
      };
      (Array.isArray(s.answers) ? s.answers : []).forEach((item, idx) => {
        const q = questionTextOf(item, idx);
        if (!seen.has(q)) { seen.add(q); questions.push(q); }
        const a = answerTextOf(item);
        row[q] = row[q] ? `${row[q]} | ${a}` : a;
      });
      return row;
    });

    const fixed = ["#", "Name", "Email", "Phone", "User Type", "Status", "Submitted At"];
    const headers = [...fixed, ...questions];
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    ws["!cols"] = headers.map((h, i) => ({ wch: i < fixed.length ? Math.min(30, Math.max(h.length + 2, 12)) : 30 }));
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(name, usedNames));
  });
};

export const exportFileName = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `questionnaire-submissions-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}.xlsx`;
};

export const downloadWorkbook = (wb) => XLSX.writeFile(wb, exportFileName(), { bookType: "xlsx" });
